import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Trash2, Edit2, User, Clock, Calendar, MessageSquare, AlertTriangle, Moon, Sun } from 'lucide-react';
import { calculateNightShiftMetrics, formatMinutes } from '@/utils/timeFormat';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  useScheduleException,
  useUpdateScheduleException,
  useDeleteScheduleException,
  useCheckWorkedDay,
} from '@/hooks/schedules/useScheduleExceptions';
import { useEmployees } from '@/hooks/useEmployees';
import { usePermissions } from '@/hooks/usePermissions';
import type { ScheduleType, UpdateScheduleExceptionDto } from '@/types/schedule.types';

interface ExceptionDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exceptionId: string | null;
}

const SCHEDULE_TYPES: { value: ScheduleType; label: string }[] = [
  { value: 'working', label: 'Рабочий день' },
  { value: 'day_off', label: 'Выходной' },
  { value: 'holiday', label: 'Праздник' },
  { value: 'reduced', label: 'Сокращённый день' },
];

const getScheduleTypeLabel = (type: string): string => {
  const found = SCHEDULE_TYPES.find((t) => t.value === type);
  return found?.label || type;
};

export const ExceptionDetailsDialog: React.FC<ExceptionDetailsDialogProps> = ({
  open,
  onOpenChange,
  exceptionId,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isWorkingDay, setIsWorkingDay] = useState(true);
  const [isReplacement, setIsReplacement] = useState(false);
  const [isOvertime, setIsOvertime] = useState(false);
  const [scheduleType, setScheduleType] = useState<ScheduleType>('working');
  const [replacementEmployeeId, setReplacementEmployeeId] = useState<string | null>(null);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmWorkedDay, setConfirmWorkedDay] = useState(false);

  const { data: exception, isLoading } = useScheduleException(exceptionId);
  const updateException = useUpdateScheduleException();
  const deleteException = useDeleteScheduleException();
  const { isAdmin } = usePermissions();
  const canForceWorkedDay = isAdmin();

  // Защита от затирания отработанного дня при редактировании: конфликт,
  // если исключение сейчас рабочее, его переводят в выходной/замену,
  // а у сотрудника уже есть отметки за эту дату
  const workedDayCheck = useCheckWorkedDay({
    employeeId: exception?.employee_id ?? null,
    date: exception?.date ?? null,
    enabled: open && isEditing,
  });
  const existingWasWorking =
    !!exception && exception.is_working_day && !exception.is_replacement;
  const workedDayConflict =
    !!workedDayCheck.data?.has_worked_shift &&
    existingWasWorking &&
    (!isWorkingDay || isReplacement);
  const workedDayCheckInLabel = workedDayCheck.data?.check_in_time
    ? format(new Date(workedDayCheck.data.check_in_time), 'HH:mm')
    : null;

  // Fetch employees for replacement dropdown
  const { data: employeesData, isLoading: isLoadingEmployees } = useEmployees({
    page: 1,
    limit: 10000,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
  } = useForm<{
    shift_start: string;
    shift_end: string;
    reason: string;
    comment: string;
  }>();

  // Watch shift times for real-time calculation in edit mode
  const shiftStart = watch('shift_start');
  const shiftEnd = watch('shift_end');

  // Calculate night shift metrics in real-time for edit mode
  const editNightMetrics = useMemo(() => {
    if (!isEditing || !isWorkingDay || isReplacement) return null;
    return calculateNightShiftMetrics(shiftStart, shiftEnd);
  }, [isEditing, shiftStart, shiftEnd, isWorkingDay, isReplacement]);

  // Populate form when exception loads
  useEffect(() => {
    if (exception) {
      const shiftStart = exception.new_shift_start
        ? exception.new_shift_start.toString().slice(0, 5)
        : '09:00';
      const shiftEnd = exception.new_shift_end
        ? exception.new_shift_end.toString().slice(0, 5)
        : '18:00';

      reset({
        shift_start: shiftStart,
        shift_end: shiftEnd,
        reason: exception.reason || '',
        comment: exception.comment || '',
      });

      setIsWorkingDay(exception.is_working_day);
      setIsReplacement(exception.is_replacement);
      setIsOvertime(exception.is_overtime ?? false);
      setScheduleType(exception.schedule_type as ScheduleType);
      setReplacementEmployeeId(exception.replacement_employee_id || null);
    }
  }, [exception, reset]);

  // Reset editing state when dialog closes
  useEffect(() => {
    if (!open) {
      setIsEditing(false);
      setSubmitError(null);
      setConfirmWorkedDay(false);
    }
  }, [open]);

  const onSubmit = async (formData: {
    shift_start: string;
    shift_end: string;
    reason: string;
    comment: string;
  }) => {
    if (!exceptionId) return;

    const dto: UpdateScheduleExceptionDto = {
      is_working_day: isWorkingDay,
      schedule_type: scheduleType,
      reason: formData.reason || null,
      comment: formData.comment || null,
      is_replacement: isReplacement,
      replacement_employee_id: isReplacement ? replacementEmployeeId : null,
      is_overtime: isOvertime,
      // Обход защиты отработанного дня — только полный админ с явным чекбоксом
      force_worked_day:
        workedDayConflict && canForceWorkedDay && confirmWorkedDay
          ? true
          : undefined,
    };

    // Add shift times only for working days
    // Backend expects HH:mm:ss format, but input type="time" gives HH:mm
    if (isWorkingDay && !isReplacement) {
      dto.new_shift_start = formData.shift_start ? `${formData.shift_start}:00` : null;
      dto.new_shift_end = formData.shift_end ? `${formData.shift_end}:00` : null;
    } else {
      dto.new_shift_start = null;
      dto.new_shift_end = null;
    }

    try {
      setSubmitError(null);
      await updateException.mutateAsync({ id: exceptionId, data: dto });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update exception:', error);
      const message =
        (error as { message?: string })?.message ||
        'Не удалось сохранить исключение';
      setSubmitError(message);
    }
  };

  const handleDelete = async () => {
    if (!exceptionId) return;

    try {
      await deleteException.mutateAsync(exceptionId);
      setIsDeleteDialogOpen(false);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to delete exception:', error);
    }
  };

  // Filter out current employee from replacement list
  const availableReplacementEmployees = employeesData?.data?.filter(
    (emp) => emp.id !== exception?.employee_id
  );

  const formattedDate = exception?.date
    ? format(new Date(exception.date), 'd MMMM yyyy (EEEE)', { locale: ru })
    : '';

  const employeeName = exception?.employee
    ? `${exception.employee.last_name} ${exception.employee.first_name}`
    : '';

  const replacementEmployeeName = exception?.replacement_employee
    ? `${exception.replacement_employee.last_name} ${exception.replacement_employee.first_name}`
    : '';

  const createdByEmail = exception?.created_by?.email || 'Неизвестно';

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Загрузка...</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!exception) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Исключение не найдено</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center text-muted-foreground">
            Запрашиваемое исключение не существует или было удалено.
          </div>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Закрыть</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Исключение в графике
            </DialogTitle>
            <DialogDescription className="flex flex-col gap-1">
              <span className="font-medium text-foreground">{employeeName}</span>
              <span>{formattedDate}</span>
            </DialogDescription>
          </DialogHeader>

          {isEditing ? (
            // Edit Mode
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Schedule Type */}
              <div className="space-y-2">
                <Label>Тип дня</Label>
                <Select
                  value={scheduleType}
                  onValueChange={(value: ScheduleType) => {
                    setScheduleType(value);
                    setIsWorkingDay(value === 'working' || value === 'reduced');
                  }}
                  disabled={isReplacement}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCHEDULE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Is Working Day Toggle */}
              <div className="flex items-center justify-between">
                <Label htmlFor="is-working-edit">Рабочий день</Label>
                <Switch
                  id="is-working-edit"
                  checked={isWorkingDay}
                  onCheckedChange={setIsWorkingDay}
                  disabled={isReplacement}
                />
              </div>

              {/* Replacement Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="is-replacement-edit">Замена сотрудника</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Сотрудник получит выходной
                  </p>
                </div>
                <Switch
                  id="is-replacement-edit"
                  checked={isReplacement}
                  onCheckedChange={(checked) => {
                    setIsReplacement(checked);
                    if (checked) {
                      // Employee being replaced gets day off
                      setIsWorkingDay(false);
                      setScheduleType('day_off');
                      // Смена сменщика по умолчанию сверхурочная
                      setIsOvertime(true);
                    }
                  }}
                />
              </div>

              {/* Overtime Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="is-overtime-edit">Сверхурочные</Label>
                  {isReplacement && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Применяется к смене заменяющего сотрудника
                    </p>
                  )}
                </div>
                <Switch
                  id="is-overtime-edit"
                  checked={isOvertime}
                  onCheckedChange={setIsOvertime}
                />
              </div>

              {/* Replacement Employee Selector */}
              {isReplacement && (
                <div className="space-y-2">
                  <Label>Заменяющий сотрудник</Label>
                  {isLoadingEmployees ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <Select
                      value={replacementEmployeeId || ''}
                      onValueChange={(value) => setReplacementEmployeeId(value || null)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите сотрудника" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableReplacementEmployees?.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.last_name} {emp.first_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* Shift Times */}
              {isWorkingDay && !isReplacement && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="shift_start_edit">Начало смены</Label>
                      <Input
                        id="shift_start_edit"
                        type="time"
                        {...register('shift_start')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="shift_end_edit">Конец смены</Label>
                      <Input
                        id="shift_end_edit"
                        type="time"
                        {...register('shift_end')}
                      />
                    </div>
                  </div>

                  {/* Night shift info - real-time calculation */}
                  {editNightMetrics && editNightMetrics.totalMinutes > 0 && (
                    <div className="flex flex-wrap items-center gap-3 text-sm bg-muted/50 p-3 rounded-md border">
                      {editNightMetrics.crossesMidnight && (
                        <span className="flex items-center gap-1.5 text-indigo-600 font-medium">
                          <Moon className="h-4 w-4" />
                          Ночная смена
                        </span>
                      )}
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <Sun className="h-4 w-4 text-amber-500" />
                        Дневных: {formatMinutes(editNightMetrics.dayMinutes)}
                      </span>
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <Moon className="h-4 w-4 text-indigo-400" />
                        Ночных: {formatMinutes(editNightMetrics.nightMinutes)}
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Reason */}
              <div className="space-y-2">
                <Label htmlFor="reason_edit">Причина</Label>
                <Input
                  id="reason_edit"
                  {...register('reason')}
                />
              </div>

              {/* Comment */}
              <div className="space-y-2">
                <Label htmlFor="comment_edit">Комментарий</Label>
                <Textarea
                  id="comment_edit"
                  rows={2}
                  {...register('comment')}
                />
              </div>

              {/* Защита от затирания отработанного дня */}
              {workedDayConflict && (
                <div className="flex items-start gap-2 text-sm p-3 rounded-md border bg-red-50 border-red-300 text-red-900">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div className="space-y-2">
                    <p className="font-medium">
                      Сотрудник уже отработал этот день
                      {workedDayCheckInLabel && (
                        <> — отметка прихода в {workedDayCheckInLabel}</>
                      )}
                    </p>
                    {canForceWorkedDay ? (
                      <>
                        <p>
                          Перевод в выходной сотрёт фактическую смену из
                          графика. Отметки прихода сохранятся, но день будет
                          показан как выходной.
                        </p>
                        <label className="flex items-start gap-2 cursor-pointer">
                          <Checkbox
                            checked={confirmWorkedDay}
                            onCheckedChange={(checked) =>
                              setConfirmWorkedDay(checked === true)
                            }
                            className="mt-0.5"
                          />
                          <span>
                            Подтверждаю: проставить выходной несмотря на
                            отметки (например, отметка ошибочна)
                          </span>
                        </label>
                      </>
                    ) : (
                      <p>
                        Перевести отработанный день в выходной нельзя —
                        фактическая смена перестанет отображаться в графике.
                        Обратитесь к администратору, если отметка ошибочна.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Ошибка сохранения */}
              {submitError && (
                <div className="flex items-start gap-2 text-sm p-3 rounded-md border bg-red-50 border-red-300 text-red-900">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>{submitError}</p>
                </div>
              )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  disabled={
                    updateException.isPending ||
                    (workedDayConflict &&
                      (!canForceWorkedDay || !confirmWorkedDay))
                  }
                >
                  {updateException.isPending ? 'Сохранение...' : 'Сохранить'}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            // View Mode
            <div className="space-y-4">
              {/* Status Badges */}
              <div className="flex gap-2 flex-wrap">
                <Badge variant={exception.is_working_day ? 'default' : 'secondary'}>
                  {exception.is_working_day ? 'Рабочий день' : 'Выходной'}
                </Badge>
                <Badge variant="outline">
                  {getScheduleTypeLabel(exception.schedule_type)}
                </Badge>
                {exception.is_overtime && (
                  <Badge variant="default" className="gap-1 bg-orange-500 hover:bg-orange-600">
                    Сверхурочные
                  </Badge>
                )}
                {exception.is_replacement && (
                  <Badge variant="destructive" className="gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Замена
                  </Badge>
                )}
              </div>

              {/* Time Info */}
              {exception.is_working_day && !exception.is_replacement && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {exception.new_shift_start?.toString().slice(0, 5) || '—'} —{' '}
                      {exception.new_shift_end?.toString().slice(0, 5) || '—'}
                    </span>
                  </div>

                  {/* Night shift info from saved data */}
                  {(exception.night_minutes > 0 || exception.day_minutes > 0) && (
                    <div className="flex flex-wrap items-center gap-3 text-sm bg-muted/50 p-3 rounded-md border">
                      {exception.crosses_midnight && (
                        <span className="flex items-center gap-1.5 text-indigo-600 font-medium">
                          <Moon className="h-4 w-4" />
                          Ночная смена
                        </span>
                      )}
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <Sun className="h-4 w-4 text-amber-500" />
                        Дневных: {formatMinutes(exception.day_minutes)}
                      </span>
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <Moon className="h-4 w-4 text-indigo-400" />
                        Ночных: {formatMinutes(exception.night_minutes)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Replacement Employee */}
              {exception.is_replacement && replacementEmployeeName && (
                <div className="flex items-center gap-2 text-sm bg-amber-50 dark:bg-amber-950 p-3 rounded-lg">
                  <User className="h-4 w-4 text-amber-600" />
                  <div>
                    <span className="text-muted-foreground">Заменяющий: </span>
                    <span className="font-medium">{replacementEmployeeName}</span>
                  </div>
                </div>
              )}

              {/* Reason */}
              {exception.reason && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Причина</Label>
                  <p className="text-sm">{exception.reason}</p>
                </div>
              )}

              {/* Comment */}
              {exception.comment && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    Комментарий
                  </Label>
                  <p className="text-sm bg-muted p-2 rounded">{exception.comment}</p>
                </div>
              )}

              {/* Created Info */}
              <div className="pt-2 border-t text-xs text-muted-foreground space-y-1">
                <div>
                  Создано: {createdByEmail}
                </div>
                <div>
                  {format(new Date(exception.created_at), 'dd.MM.yyyy HH:mm', { locale: ru })}
                </div>
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="gap-1"
                >
                  <Trash2 className="h-4 w-4" />
                  Удалить
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="gap-1"
                >
                  <Edit2 className="h-4 w-4" />
                  Редактировать
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => onOpenChange(false)}
                >
                  Закрыть
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить исключение?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить это исключение из графика?
              <br />
              <span className="font-medium">{employeeName}</span> — {formattedDate}
              <br /><br />
              Это действие нельзя отменить. После удаления для сотрудника будет
              использоваться базовый график.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteException.isPending ? 'Удаление...' : 'Удалить'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ExceptionDetailsDialog;
